import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Truck, 
  Search, 
  Plus, 
  Edit, 
  Trash2,
  X,
  Save,
  Phone,
  Mail
} from 'lucide-react';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([
    {
      id: 1,
      name: 'Viñedos del Sur S.A.',
      rut: '211234560018',
      address: 'Ruta 8 Km 23.5, Canelones',
      phone: '+598 99 123 456',
      email: 'ventas@vineodosdelsur.com.uy',
      paymentTerms: '30 días',
      products: 'Vinos Tannat, Merlot'
    },
    {
      id: 2,
      name: 'Aceites Premium Uruguay',
      rut: '219876540012',
      address: 'Av. Italia 2450, Montevideo',
      phone: '+598 98 765 432',
      email: 'contacto@aceitespremium.com',
      paymentTerms: '45 días',
      products: 'Aceite de Oliva, Aceite de Girasol'
    },
    {
      id: 3,
      name: 'Quesería Artesanal',
      rut: '215555550015',
      address: 'Camino Vecinal 123, Colonia',
      phone: '+598 97 111 222',
      email: 'info@queseriaartesanal.uy',
      paymentTerms: '15 días',
      products: 'Quesos semicurados, Quesos frescos'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    rut: '',
    address: '',
    phone: '',
    email: '',
    paymentTerms: '',
    products: ''
  });

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.rut.includes(searchTerm) ||
    supplier.products.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateForm = () => {
    if (!formData.name || !formData.rut || !formData.email) {
      alert('Por favor complete los campos obligatorios: Nombre, RUT y Email');
      return false;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Por favor ingrese un email válido');
      return false;
    }

    // Validar formato de teléfono (opcional si está presente)
    if (formData.phone) {
      const phoneRegex = /^\+?[0-9\s\-()]+$/;
      if (!phoneRegex.test(formData.phone)) {
        alert('Por favor ingrese un teléfono válido');
        return false;
      }
    }

    return true;
  };

  const handleAddSupplier = () => {
    if (!validateForm()) return;

    const newSupplier = {
      id: suppliers.length + 1,
      ...formData
    };

    setSuppliers([...suppliers, newSupplier]);
    setShowAddModal(false);
    resetForm();
  };

  const handleEditSupplier = () => {
    if (!validateForm()) return;

    setSuppliers(suppliers.map(s =>
      s.id === selectedSupplier.id ? { ...s, ...formData } : s
    ));
    setShowEditModal(false);
    resetForm();
  };

  const handleDeleteSupplier = (id) => {
    if (window.confirm('¿Está seguro de eliminar este proveedor?')) {
      setSuppliers(suppliers.filter(s => s.id !== id));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rut: '',
      address: '',
      phone: '',
      email: '',
      paymentTerms: '',
      products: ''
    });
    setSelectedSupplier(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Truck className="h-8 w-8" style={{color: '#D4AF37'}} />
            Gestión de Proveedores
          </h1>
          <p className="text-gray-600 mt-1">Administra tus proveedores y contactos</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg"
          style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
        >
          <Plus className="h-5 w-5" />
          Nuevo Proveedor
        </button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-sm font-medium text-gray-600">Total Proveedores</h3>
          <p className="text-3xl font-bold mt-2" style={{color: '#D4AF37'}}>{suppliers.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-sm font-medium text-gray-600">Proveedores Activos</h3>
          <p className="text-3xl font-bold mt-2 text-green-600">{suppliers.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-sm font-medium text-gray-600">Categorías</h3>
          <p className="text-3xl font-bold mt-2 text-gray-800">4</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, RUT o productos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Suppliers Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}>
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Proveedor</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">RUT</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Contacto</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Condiciones Pago</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white">Productos</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-white">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-800">{supplier.name}</p>
                      <p className="text-sm text-gray-500">{supplier.address}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-mono text-gray-600">{supplier.rut}</td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4" />
                        {supplier.phone}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        {supplier.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      {supplier.paymentTerms}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{supplier.products}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedSupplier(supplier);
                          setFormData(supplier);
                          setShowEditModal(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSupplier(supplier.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-800">
                {showAddModal ? 'Nuevo Proveedor' : 'Editar Proveedor'}
              </h3>
              <button onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
                <X className="h-6 w-6 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre o Razón Social <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Nombre del proveedor"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    RUT <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.rut}
                    onChange={(e) => setFormData({...formData, rut: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="211234560018"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="+598 99 123 456"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Dirección completa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Correo Electrónico <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="email@proveedor.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condiciones de Pago</label>
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData({...formData, paymentTerms: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="">Seleccionar</option>
                  <option value="Contado">Contado</option>
                  <option value="15 días">15 días</option>
                  <option value="30 días">30 días</option>
                  <option value="45 días">45 días</option>
                  <option value="60 días">60 días</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Productos que Suministra</label>
                <textarea
                  value={formData.products}
                  onChange={(e) => setFormData({...formData, products: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows="3"
                  placeholder="Ej: Vinos Tannat, Merlot, Chardonnay"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={showAddModal ? handleAddSupplier : handleEditSupplier}
                className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
              >
                <Save className="h-5 w-5" />
                {showAddModal ? 'Guardar Proveedor' : 'Guardar Cambios'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;
